<template>
  <div
    class="job-status"
    :class="{
      'hover-effect': showHoverEffect,
      [statusClass]: Boolean(statusClass),
      loading,
      disabled,
    }"
  >
    <div v-if="loading" class="loading-shimmer" />
    <div class="flex items-center">
      <v-icon v-if="statusIcon" :icon="statusIcon" size="15px" />
      {{ loading ? loadingText : statusText }}
      <slot name="append" />
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  statusIcon: {
    type: String,
    default: '',
  },
  statusText: {
    type: String,
    default: '',
  },
  statusClass: {
    type: String,
    default: '',
  },
  showHoverEffect: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const loadingText = ref('Updating');

let interval;

watch(
  () => props.loading,
  (newVal) => {
    clearInterval(interval);
    if (newVal) {
      let dots = 0;
      interval = setInterval(() => {
        dots = (dots + 1) % 4;
        loadingText.value = `Updating${'.'.repeat(dots)}`;
      }, 375);
    } else {
      loadingText.value = 'Updating';
    }
  },
  { immediate: true },
);
</script>

<style scoped lang="less">
.job-status {
  padding: 2px 8px;
  height: 24px;
  max-height: 24px;
  border-radius: 30px;
  gap: 2px;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  position: relative;

  &.draft, &.email {
    background: @documentstatus-draft;
    color: @white;
    border-color: @documentstatus-draft;

    &.hover-effect:not(.disabled):not(.loading):hover {
      background: darken(@documentstatus-draft, 5%);
      border-color: darken(@documentstatus-draft, 5%);
    }
  }

  &.draft-private {
    background: @white;
    color: @documentstatus-draft;
    border-color: @stroke-default;

    &.hover-effect:not(.disabled):not(.loading):hover {
      background: darken(@white, 5%);
    }
  }

  &.flagship {
    background: @documentstatus-flagship;
    color: @white;
    border-color: @documentstatus-flagship;

    &.hover-effect:not(.disabled):not(.loading):hover {
      background: darken(@documentstatus-flagship, 5%);
      border-color: darken(@documentstatus-flagship, 5%);
    }
  }

  &.flagship-hidden {
    background: @white;
    color: @documentstatus-flagship;
    border-color: @stroke-default;

    &.hover-effect:not(.disabled):not(.loading):hover {
      background: darken(@white, 5%);
    }
  }

  &.open {
    background: @documentstatus-published;
    color: @white;
    border-color: @documentstatus-published;

    &.hover-effect:not(.disabled):not(.loading):hover {
      background: darken(@documentstatus-published, 5%);
      border-color: darken(@documentstatus-published, 5%);
    }
  }

  &.unpublished {
    background: @white;
    color: @documentstatus-published;
    border-color: @stroke-default;

    &.hover-effect:not(.disabled):not(.loading):hover {
      background: darken(@white, 5%);
    }
  }

  &.template {
    background: @documentstatus-template;
    color: @white;
    border-color: @documentstatus-template;

    &.hover-effect:not(.disabled):not(.loading):hover {
      background: darken(@documentstatus-template, 5%);
      border-color: darken(@documentstatus-template, 5%);
    }
  }

  &.template-hidden {
    background: @white;
    color: @documentstatus-template;
    border-color: @stroke-default;

    &.hover-effect:not(.disabled):not(.loading):hover {
      background: darken(@white, 5%);
    }
  }

  &.loading.hover-effect:not(.disabled):not(.loading):hover {
    background: @white;
    border-color: @stroke-default;
  }

  &.disabled {
    cursor: not-allowed !important;
    opacity: 0.6;
  }
}

.loading-shimmer {
  position: absolute;
  top: 0;
  left: -100%;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
</style>
